import React, { useEffect, useRef, useState } from "react";
import { API_URL } from "../config";

const MAX_BYTES = 2_800_000; // сервер принимает до 3 МБ

/** Снимок видимой части страницы. Библиотека грузится только при открытии окна; чужие скрипты не подключаются. */
async function captureScreenshot() {
  const { default: html2canvas } = await import("html2canvas");
  for (const scale of [0.7, 0.5, 0.35]) {
    const canvas = await html2canvas(document.body, {
      useCORS: true, logging: false, scale,
      x: window.scrollX, y: window.scrollY, width: window.innerWidth, height: window.innerHeight,
      windowWidth: document.documentElement.clientWidth,
      ignoreElements: el => el.dataset && el.dataset.noScreenshot === "true",
    });
    const url = canvas.toDataURL("image/png");
    if (url.length * 0.75 <= MAX_BYTES) return url;
  }
  throw new Error("too_large");
}

/** «Сообщить об ошибке»: текст, адрес страницы и (по желанию) снимок экрана уходят на почту редакции. */
export default function FeedbackModal({ onClose, user, token }) {
  const [text, setText] = useState("");
  const [contact, setContact] = useState(user?.email || "");
  const [shot, setShot] = useState(null);
  const [withShot, setWithShot] = useState(true);
  const [capturing, setCapturing] = useState(true);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const page = useRef(window.location.pathname + window.location.search);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    captureScreenshot().then(setShot).catch(() => setShot(null)).finally(() => setCapturing(false));
  }, []);

  const submit = async e => {
    e.preventDefault();
    if (text.trim().length < 3) { setError("Опишите проблему хотя бы в нескольких словах."); return; }
    setSending(true); setError("");
    try {
      const r = await fetch(`${API_URL}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ text: text.trim(), page: page.current, contact: contact.trim() || null, screenshot: withShot && shot ? shot : null }),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        throw new Error(typeof d.detail === "string" ? d.detail : "Не удалось отправить. Напишите нам на info@svrach.ru");
      }
      setSent(true);
      setTimeout(onClose, 2200);
    } catch (err) {
      setError(err.message || "Не удалось отправить. Напишите нам на info@svrach.ru");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" data-no-screenshot="true" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-box feedback-box" role="dialog" aria-modal="true" aria-label="Сообщить об ошибке">
        <div className="modal-header">
          <h2>Сообщить об ошибке</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Закрыть">✕</button>
        </div>
        {sent ? (
          <p className="feedback-sent">Сообщение отправлено в редакцию. Спасибо!</p>
        ) : (
          <form className="auth-form" onSubmit={submit}>
            <label>Что произошло? Что вы ожидали увидеть?</label>
            <textarea value={text} onChange={e => setText(e.target.value)} rows={5} maxLength={4000} autoFocus
              placeholder="Например: на странице лекции не запускается видео" />
            <label>Как с вами связаться (по желанию)</label>
            <input type="email" value={contact} onChange={e => setContact(e.target.value)} placeholder="E-mail для ответа" />
            <div className="feedback-shot">
              {capturing && <span>Снимаем экран…</span>}
              {!capturing && shot && (
                <>
                  <label className="feedback-shot-check">
                    <input type="checkbox" checked={withShot} onChange={e => setWithShot(e.target.checked)} /> Приложить снимок экрана
                  </label>
                  {withShot && <img src={shot} alt="Снимок экрана, который будет приложен" />}
                </>
              )}
              {!capturing && !shot && <span>Снимок экрана сделать не удалось — отправим только описание.</span>}
            </div>
            <p className="feedback-note">К сообщению добавятся адрес страницы и сведения о браузере{user ? ", а также ваш аккаунт" : ""}. Сообщение получает только редакция.</p>
            {error && <div className="auth-error">{error}</div>}
            <button type="submit" className="auth-submit-btn" disabled={sending || text.trim().length < 3}>
              {sending ? "Отправка…" : "Отправить"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
